// @flow

import * as React from 'react';
import {useState} from 'react';

import {Handle, Position, useUpdateNodeInternals} from 'react-flow-renderer';
import {FormattedMessage, defineMessages} from 'react-intl';
import {renderMarkdocAsReact} from 'docs/markdoc';

import Box from 'sail/Box';
import {BodyAlt, BodyMono, Body, Text} from 'sail/Text';
import Icon from 'sail/Icon';
import {chevronUp, chevronDown} from 'sail/Icon/Assets';

import './EntityDiagram.scss';
import classNames from 'classnames';

import type {RenderNodes} from '@markdoc/markdoc';
import {GeneratorSourceHandle, GeneratorTargetHandle} from '../Handles';

const messages = defineMessages({
  hide: {
    id: 'docsShell.diagram.entityDiagram.hide',
    description: 'A label prompting a user to hide a collapsible section',
    defaultMessage: 'hide sample arguments',
  },
  show: {
    id: 'docsShell.diagram.entityDiagram.show',
    description: 'A label prompting a user to show a collapsed section',
    defaultMessage: 'show sample arguments',
  },
});

type EntityAttribute = {|
  name: string,
  value: string,
  handle: string | null,
|};

type EntityDiagramNodeAttributeRowProps = {|
  attribute: EntityAttribute,
  handles: Array<string>,
|};

const EntityDiagramNodeAttributeRow = ({
  attribute,
  handles,
}: EntityDiagramNodeAttributeRowProps) => {
  const handle = attribute.handle ? (
    <>
      <Handle
        type="source"
        id={attribute.handle}
        position={Position.Right}
        style={{top: '50%'}}
      />
      <GeneratorSourceHandle id={attribute.handle} />
    </>
  ) : null;

  return (
    <Box
      className="EntityDiagramNode-row"
      divider={{side: 'top', color: 'light'}}
      flex={{justifyContent: 'space-between'}}
      padding={{vertical: 8, horizontal: 24}}
    >
      <Box margin={{right: 32}}>
        <Body>{attribute.name}</Body>
      </Box>
      <Box style={{position: 'relative'}}>
        <BodyMono>{renderMarkdocAsReact(attribute.value)}</BodyMono>
      </Box>
      {handle}
    </Box>
  );
};

type EntityDiagramNodeData = {|
  header: RenderNodes,
  attributes?: Array<EntityAttribute>,
  handles?: Array<string>,
|};

type EntityDiagramNodeProps = {|
  data: EntityDiagramNodeData,
  id: string,
|};

export const EntityDiagramNode = ({data, id}: EntityDiagramNodeProps) => {
  const {header, attributes, handles} = data;
  const [open, setOpen] = useState(true);
  const updateNodeInternals = useUpdateNodeInternals();

  if (!header) {
    return null;
  }

  return (
    <Box
      className="EntityDiagramNode"
      flex={{
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'column',
      }}
      background="white"
    >
      <Box className="EntityDiagramNode-border">
        <Handle
          type="target"
          id="left"
          position={Position.Left}
          style={{top: '50%'}}
        />
        <GeneratorTargetHandle />
      </Box>
      <Box className="EntityDiagramNode-content" padding={{vertical: 12}}>
        <Box
          className="EntityDiagramNode-header"
          flex={{direction: 'column'}}
          divider={{side: 'bottom', color: 'light'}}
          padding={{bottom: 12}}
          margin={{horizontal: 24}}
        >
          <Text
            align="center"
            weight="bold"
            typeface="mono"
            size={13}
            lineHeight={20}
          >
            {renderMarkdocAsReact(header)}
          </Text>
          <Handle
            type="source"
            position={Position.Right}
            id="right"
            style={{top: '50%', right: 0}}
          />
          {!open &&
            handles?.map((h) => (
              <Handle
                key={h}
                type="source"
                position={Position.Right}
                id={h}
                style={{top: '50%', right: 0}}
              />
            ))}
          {handles?.length === 0 ? (
            <GeneratorSourceHandle id={'generator'} />
          ) : null}
        </Box>
        <Box
          className="EntityDiagramNode-toggle"
          flex={{justifyContent: 'center', alignItems: 'center'}}
          padding={{vertical: 8}}
          onClick={() => {
            setOpen(!open);
            // We have to manually tell react-flow-renderer that we updated the handles on the node
            // if the node is closed, we redraw all the attribute handles at the top of the node (since the attributes are hidden)
            updateNodeInternals(id);
          }}
        >
          <Box margin={{right: 8}}>
            <Icon icon={open ? chevronUp : chevronDown} color={'gray600'} />
          </Box>
          <BodyAlt>
            {open ? (
              <FormattedMessage {...messages.hide} />
            ) : (
              <FormattedMessage {...messages.show} />
            )}
          </BodyAlt>
        </Box>
        {attributes && (
          <Box
            className={classNames(
              'EntityDiagramNode-table',
              `EntityDiagramNode-table--${open ? 'open' : 'closed'}`,
            )}
          >
            {attributes.map((a, i) => (
              <EntityDiagramNodeAttributeRow
                key={`${a.name}-${i}`}
                attribute={a}
                handles={open && handles ? handles : []}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
